import type { Chef, Language, Recipe, User } from '../types';
import { extractFacebookPageSlug, isFacebookDomain } from './facebookDisplay';

const HEBREW_RE = /[\u0590-\u05FF]/;

export function hasHebrewText(text: string): boolean {
    return HEBREW_RE.test(text);
}

export function getChefDisplayName(chef: Chef, userLanguage: Language): string {
    if (userLanguage === chef.originLanguage && chef.name?.trim()) {
        return chef.name.trim();
    }
    return chef.englishName;
}

export function normalizeChefSearchText(text: string): string {
    return text
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9\u0590-\u05FF\s]/g, '')
        .trim();
}

export function chefMatchesQuery(chef: Chef, query: string, userLanguage: Language): boolean {
    const q = normalizeChefSearchText(query);
    if (!q) return true;
    const fields = [
        chef.name,
        chef.englishName,
        chef.nickname.replace(/_/g, ' '),
        getChefDisplayName(chef, userLanguage),
    ];
    return fields.some((f) => normalizeChefSearchText(f).includes(q));
}

export function recipeMatchesChefFilter(
    recipe: Recipe,
    chefId: string,
    chefs: Chef[],
    users: User[] = []
): boolean {
    if (recipe.chefId === chefId) return true;
    const chef = chefs.find((c) => c.id === chefId);
    if (chef?.linkedUserId && recipe.ownerId === chef.linkedUserId) return true;
    const owner = users.find((u) => u.uid === recipe.ownerId);
    return owner?.chefId === chefId;
}

export function normalizeWebsiteUrl(website?: string): string | null {
    if (!website?.trim()) return null;

    let trimmed = website.trim().replace(/\s+/g, '');

    if (/^https?:\/\//i.test(trimmed)) {
        try {
            return new URL(trimmed).href;
        } catch {
            return null;
        }
    }

    if (trimmed.startsWith('//')) {
        trimmed = `https:${trimmed}`;
    } else {
        trimmed = `https://${trimmed.replace(/^\/+/, '')}`;
    }

    try {
        const parsed = new URL(trimmed);
        if (!parsed.hostname) return null;
        return parsed.href;
    } catch {
        return null;
    }
}

/** Validates a link and opens it in a new tab, adding https:// when missing. */
export function openExternalLink(rawUrl: string): boolean {
    const href = normalizeWebsiteUrl(rawUrl);
    if (!href) return false;
    window.open(href, '_blank', 'noopener,noreferrer');
    return true;
}

/**
 * Returns all of a chef's links, merging the modern `websites` array with the
 * legacy single `website` field, trimmed and de-duplicated (case-insensitive).
 */
export function getChefWebsites(chef: Pick<Chef, 'website' | 'websites'>): string[] {
    const result: string[] = [];
    const seen = new Set<string>();
    const add = (value?: string) => {
        const trimmed = value?.trim();
        if (!trimmed) return;
        const key = trimmed.toLowerCase();
        if (seen.has(key)) return;
        seen.add(key);
        result.push(trimmed);
    };
    (chef.websites ?? []).forEach(add);
    add(chef.website);
    return result;
}

export function inferChefOrigin(name: string): Pick<Chef, 'originLanguage' | 'originCountry'> {
    if (hasHebrewText(name)) {
        return { originLanguage: 'he', originCountry: 'Israel' };
    }
    return { originLanguage: 'en' };
}

export function isChefActive(chef: Chef): boolean {
    return !chef.disabled;
}

export function normalizeChefNickname(value: string): string {
    return value
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[_\s-]/g, '')
        .replace(/[^a-z0-9\u0590-\u05FF]/g, '');
}

export function chefNicknameVariants(chef: Chef): string[] {
    const variants = new Set<string>();
    const add = (value?: string) => {
        const normalized = value ? normalizeChefNickname(value) : '';
        if (normalized) variants.add(normalized);
    };
    add(chef.nickname);
    add(chef.id);
    add(chef.nickname.replace(/_/g, ' '));
    add(chef.nickname.replace(/_/g, ''));
    return Array.from(variants);
}

export function canUserPickChef(chef: Chef, userId?: string): boolean {
    if (!chef.linkedUserId) return true;
    return !!userId && chef.linkedUserId === userId;
}

export function getPickableChefs(chefs: Chef[], userId?: string): Chef[] {
    return getActiveChefs(chefs).filter((chef) => canUserPickChef(chef, userId));
}

export function findChefByReservedNickname(chefs: Chef[], nicknameQuery: string): Chef | null {
    const normalized = normalizeChefNickname(nicknameQuery);
    if (!normalized) return null;
    return chefs.find((chef) => chefNicknameVariants(chef).includes(normalized)) ?? null;
}

export function canUserUseReservedChefNickname(chef: Chef, userId?: string, userChefId?: string): boolean {
    if (!userId) return false;
    if (chef.linkedUserId === userId) return true;
    return !!userChefId && userChefId === chef.id;
}

export function isUserNicknameReservedByChef(
    chefs: Chef[],
    nicknameQuery: string,
    userId?: string,
    userChefId?: string
): boolean {
    const trimmed = nicknameQuery.trim();
    if (!trimmed) return false;
    const chef = findChefByReservedNickname(chefs, trimmed);
    if (!chef?.linkedUserId) return false;
    return !canUserUseReservedChefNickname(chef, userId, userChefId);
}

export function isNicknameLockedForUser(chefs: Chef[], nicknameQuery: string, userId?: string): boolean {
    const normalized = normalizeChefNickname(nicknameQuery);
    if (!normalized) return false;

    return chefs.some((chef) => {
        if (!chef.linkedUserId || canUserPickChef(chef, userId)) return false;
        return chefNicknameVariants(chef).includes(normalized);
    });
}

export function findChefByNickname(chefs: Chef[], nicknameQuery: string, userId?: string): Chef | null {
    const normalized = normalizeChefNickname(nicknameQuery);
    if (!normalized) return null;

    for (const chef of getPickableChefs(chefs, userId)) {
        if (chefNicknameVariants(chef).includes(normalized)) {
            return chef;
        }
    }
    return null;
}

export function getLinkableChefs(chefs: Chef[]): Chef[] {
    return getActiveChefs(chefs).filter((chef) => !chef.linkedUserId);
}

/**
 * Returns active chefs that are actually credited on at least one (non-deleted)
 * recipe, along with how many recipes each is credited on. Used by the filter
 * UI so we never surface chefs the user can't actually filter to.
 */
export function getChefsWithRecipes(
    chefs: Chef[],
    recipes: Recipe[],
    users: User[] = []
): { chef: Chef; recipeCount: number }[] {
    return getActiveChefs(chefs)
        .map((chef) => ({
            chef,
            recipeCount: recipes.filter(
                (r) => !r.isDeleted && recipeMatchesChefFilter(r, chef.id, chefs, users)
            ).length,
        }))
        .filter((entry) => entry.recipeCount > 0)
        .sort((a, b) => b.recipeCount - a.recipeCount);
}

export function getActiveChefs(chefs: Chef[]): Chef[] {
    return chefs.filter(isChefActive);
}

export function extractDomain(urlOrHost: string): string | null {
    try {
        const withProtocol = /^https?:\/\//i.test(urlOrHost) ? urlOrHost : `https://${urlOrHost.trim()}`;
        const hostname = new URL(withProtocol).hostname.toLowerCase().replace(/^www\./, '');
        return hostname || null;
    } catch {
        return null;
    }
}

function domainsMatch(linkDomain: string, chefDomain: string): boolean {
    if (linkDomain === chefDomain) return true;
    return linkDomain.endsWith(`.${chefDomain}`);
}

/** Instagram profile path segments that are not usernames. */
const INSTAGRAM_RESERVED_SEGMENTS = new Set([
    'p', 'reel', 'reels', 'tv', 'stories', 'explore', 'accounts', 'direct', 'about', 'legal', 'developer',
]);

export function isInstagramDomain(domain: string | null): boolean {
    if (!domain) return false;
    return domain === 'instagram.com' || domain.endsWith('.instagram.com') || domain === 'instagr.am';
}

export function findChefByFacebookPageSlug(chefs: Chef[], pageSlug: string, userId?: string): Chef | null {
    const target = pageSlug.trim().toLowerCase();
    if (!target) return null;

    for (const chef of getPickableChefs(chefs, userId)) {
        for (const chefLink of getChefWebsites(chef)) {
            if (!isFacebookDomain(extractDomain(chefLink))) continue;
            if (extractFacebookPageSlug(chefLink) === target) {
                return chef;
            }
        }
    }
    return null;
}

/** Normalizes an Instagram handle: strips leading @, lowercases, drops trailing slashes. */
export function normalizeInstagramUsername(value?: string): string | null {
    if (!value?.trim()) return null;
    const cleaned = value.trim().toLowerCase().replace(/^@/, '').replace(/\/+$/, '').trim();
    return /^[a-z0-9._]+$/.test(cleaned) ? cleaned : null;
}

/** Extracts the profile username from an Instagram URL, or null for post/reel/non-profile URLs. */
export function extractInstagramUsername(urlOrHandle: string): string | null {
    if (!urlOrHandle?.trim()) return null;
    const domain = extractDomain(urlOrHandle);
    if (!isInstagramDomain(domain)) {
        // Allow a bare handle (e.g. "@danareicher" or "danareicher") when it isn't a URL.
        return /[./]/.test(urlOrHandle) && !urlOrHandle.startsWith('@') ? null : normalizeInstagramUsername(urlOrHandle);
    }
    try {
        const withProtocol = /^https?:\/\//i.test(urlOrHandle) ? urlOrHandle : `https://${urlOrHandle.trim()}`;
        const segments = new URL(withProtocol).pathname.split('/').map((s) => s.trim()).filter(Boolean);
        if (segments.length === 0) return null;
        const username = normalizeInstagramUsername(segments[0]);
        if (!username || INSTAGRAM_RESERVED_SEGMENTS.has(username)) return null;
        return username;
    } catch {
        return null;
    }
}

export function findChefByUrl(chefs: Chef[], url: string, userId?: string): Chef | null {
    const linkDomain = extractDomain(url);
    if (!linkDomain) return null;

    const linkInstagramUser = isInstagramDomain(linkDomain) ? extractInstagramUsername(url) : null;
    const linkFacebookPage = isFacebookDomain(linkDomain) ? extractFacebookPageSlug(url) : null;

    for (const chef of getPickableChefs(chefs, userId)) {
        for (const chefLink of getChefWebsites(chef)) {
            const chefDomain = extractDomain(chefLink);
            if (!chefDomain) continue;

            // Instagram profiles all share the instagram.com domain, so match by handle instead.
            if (isInstagramDomain(chefDomain)) {
                const chefInstagramUser = extractInstagramUsername(chefLink);
                if (linkInstagramUser && chefInstagramUser && linkInstagramUser === chefInstagramUser) {
                    return chef;
                }
                continue;
            }

            if (isFacebookDomain(chefDomain)) {
                const chefFacebookPage = extractFacebookPageSlug(chefLink);
                if (linkFacebookPage && chefFacebookPage && linkFacebookPage === chefFacebookPage) {
                    return chef;
                }
                continue;
            }

            if (domainsMatch(linkDomain, chefDomain)) {
                return chef;
            }
        }
    }
    return null;
}

/** Finds a chef whose website is the Instagram profile matching the given handle. */
export function findChefByInstagramUsername(chefs: Chef[], username: string, userId?: string): Chef | null {
    const target = normalizeInstagramUsername(username);
    if (!target) return null;

    for (const chef of getPickableChefs(chefs, userId)) {
        for (const chefLink of getChefWebsites(chef)) {
            if (!isInstagramDomain(extractDomain(chefLink))) continue;
            if (extractInstagramUsername(chefLink) === target) {
                return chef;
            }
        }
    }
    return null;
}

export function findChefByUrls(chefs: Chef[], urls: readonly string[], userId?: string): Chef | null {
    for (const url of urls) {
        const trimmed = url.trim();
        if (!trimmed) continue;
        const chef = findChefByUrl(chefs, trimmed, userId);
        if (chef) return chef;
    }
    return null;
}
